/**
 * The signer API: what the signing page and the server say to each other.
 * Every path is under /api/s/{token}. Plain JSON types — no functions — so both sides import them.
 *
 *   GET    /api/s/{token}                         → LinkInfo          (no session needed; reveals little)
 *   POST   /api/s/{token}/code                    → CodeSent          (sends the 6-digit email code)
 *   POST   /api/s/{token}/verify   VerifyRequest  → VerifyResult      (sets the session cookie)
 *   GET    /api/s/{token}/session                 → SessionView       (everything below needs the session)
 *   POST   /api/s/{token}/consent  ConsentRequest → SessionView
 *   POST   /api/s/{token}/documents/{id}/viewed   ViewedRequest → 204
 *   GET    /api/s/{token}/documents/{id}/file     → application/pdf   (the original: letter, or the blank form)
 *   POST   /api/s/{token}/documents/{id}/accept   → SessionView
 *   POST   /api/s/{token}/forms/{id}/choose       ChooseFormRequest → SessionView
 *   PATCH  /api/s/{token}/forms/{id}/values       SaveValuesRequest → SaveValuesResult
 *   POST   /api/s/{token}/review                  → SessionView
 *   POST   /api/s/{token}/signature               AdoptSignatureRequest → SessionView
 *   GET    /api/s/{token}/documents/{id}/preview  → application/pdf + header X-Preview-Sha256
 *   POST   /api/s/{token}/documents/{id}/certification-shown → 204
 *   POST   /api/s/{token}/documents/{id}/sign     SignRequest → SessionView
 *   GET    /api/s/{token}/documents/{id}/download → application/pdf   (the sealed copy, after completion)
 *   POST   /api/s/{token}/decline  DeclineRequest → LinkInfo
 *   POST   /api/s/{token}/paper                   → LinkInfo
 *
 * Errors: { error: ErrorCode, message } with a fitting HTTP status. A dead or unknown link is always
 * the same 404 `invalid_link`, so an outsider learns nothing from it.
 */
import type { SignerProgress, SignerStep } from "../envelope/state.js";
import type { Policy, Values } from "../rules/types.js";
import type { Validation } from "../rules/engine.js";

export type LinkState = "ready" | "expired" | "paused" | "completed" | "voided" | "declined" | "paper";

export interface Branding {
  /** Short name shown in the header, e.g. "Golden Sparrow". */
  name: string;
  legalName: string;
  /** Accent colour for the brand mark (hex). */
  color?: string;
  logoUrl?: string;
}

export interface SenderInfo extends Branding {
  contactName: string;
  contactEmail: string;
}

export interface LinkInfo {
  state: LinkState;
  sender: SenderInfo;
  signer: { firstName: string; maskedEmail: string };
  expiresAt: string;
  /** True when this browser already holds a live session for the link. */
  session: boolean;
  /** For "completed": when, so the page can say so. */
  completedAt?: string;
}

export interface CodeSent {
  sentTo: string;
  expiresAt: string;
  /** No new code before this time. */
  resendAt: string;
}

export interface VerifyRequest { code: string }
export type VerifyResult = { ok: true } | { ok: false; reason: "wrong" | "expired" | "paused"; triesLeft?: number };

export interface ConsentText {
  version: string;
  sha256: string;
  title: string;
  /** The short version shown first. */
  summary: { icon: "paper" | "undo" | "doc" | "mail" | "device" | "download"; title: string; text: string }[];
  /** The full disclosure. */
  sections: { heading: string; text: string }[];
  agreeLabel: string;
  agreeHelp: string;
}

/** A place on a page, in percent of the page from the top-left. */
export interface PageRect { page: number; l: number; t: number; w: number; h: number }

export interface FieldSpot extends PageRect {
  id: string;
  role: "company" | "signer";
  kind: "signature" | "date" | "name";
}

export interface LetterDoc {
  id: string;
  kind: "letter";
  title: string;
  version: string;
  pages: number;
  sha256: string;
  /** The key terms, shown beside the letter. */
  terms: { label: string; value: string }[];
  spots: FieldSpot[];
  /** Signed for the company when it was sent… */
  companySigned?: { name: string; title?: string; at: string };
  /** …or to be countersigned by the company after the signer (its printed name and title). */
  companyCountersigns?: { name: string; title?: string };
  /** Merged-in values, for the "filled in for you" underline (page + rect per value). */
  mergedSpots: (PageRect & { key: string })[];
}

export interface FormChoice {
  /** "no" = not a U.S. person → W-8BEN; "yes" → W-9. */
  value: "no" | "yes";
  label: string;
  detail: string;
  /** null when this build can't serve that form yet. */
  pack: string | null;
}

export interface FormDoc {
  id: string;
  kind: "form";
  title: string;
  chooser: { question: string; help: string; choices: FormChoice[] };
  /** "irs.w8ben@2021-10" once chosen. The page gets the pack itself from @gsignatures/core. */
  pack?: string;
  policy?: Policy;
  values: Values;
  validation?: Validation;
  /** The account's facts used by flag checks are never sent to the signer. */
  savedAt?: string;
}

/** Pictures the signer adds — an ID proof (owner, 2026-10-25). Sealed into a PDF with the rest; never signed. */
export interface UploadDoc {
  id: string;
  kind: "upload";
  title: string;
  /** What to photograph, in the sender's words. */
  help: string;
  required: boolean;
  /** One or two pictures (front and back). */
  maxFiles: number;
  files: UploadedFile[];
}

export interface UploadedFile {
  id: string;
  name: string;
  sha256: string;
  bytes: number;
  at: string;
}

export interface UploadFileRequest {
  /** A JPEG or PNG as a data URL — the page shrinks a photo to well under the limit first. */
  dataUrl: string;
  name?: string;
}

export type AdoptedSignature = { kind: "type"; text: string; font: SignatureFontId } | { kind: "image"; url: string };

export interface SessionView {
  envelope: { id: string; status: string; sentAt: string; expiresAt: string };
  sender: SenderInfo;
  signer: { name: string; firstName: string; maskedEmail: string };
  step: SignerStep;
  progress: SignerProgress;
  consent: ConsentText;
  documents: (LetterDoc | FormDoc | UploadDoc)[];
  signature?: AdoptedSignature;
  /** Session end, so the page can warn before it lapses. */
  sessionExpiresAt: string;
}

export interface ConsentRequest { version: string }
export interface ViewedRequest { sha256: string }
export interface ChooseFormRequest { usPerson: boolean }
export interface SaveValuesRequest { values: Values }
export interface SaveValuesResult { values: Values; validation: Validation; savedAt: string }
export type AdoptSignatureRequest = { kind: "type"; text: string; font: SignatureFontId } | { kind: "image"; dataUrl: string };
export interface SignRequest { previewSha256: string }
export interface DeclineRequest { reason?: string }

export type ErrorCode =
  | "invalid_link"
  | "session_required"
  | "wrong_step"
  | "invalid_values"
  | "preview_mismatch"
  | "rate_limited"
  | "paused"
  | "not_found"
  | "conflict"
  | "bad_request"
  | "server_error";

export interface ApiError { error: ErrorCode; message: string; details?: unknown }

/** Typed-signature styles. The same font files are embedded in the sealed PDF. */
export const SIGNATURE_FONTS = [
  { id: "homemade-apple", family: "Homemade Apple", size: 26 },
  { id: "caveat", family: "Caveat", size: 34 },
  { id: "mrs-saint-delafield", family: "Mrs Saint Delafield", size: 40 },
  { id: "dancing-script", family: "Dancing Script", size: 32 },
] as const;
export type SignatureFontId = (typeof SIGNATURE_FONTS)[number]["id"];
